import { CloseOutlined, EditTwoTone, SaveOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row } from "antd";
import PracticesService, { ISequenceReviewInitialValues, ISequenceReviewFormValues, PracticeUpdateTypes } from "../../Services/PracticesService";
import React, { useState } from "react";
import SequenceSection from "./SequenceSections";
import { adminContext } from "../../Common/PageRoute";
import ReviewRequest from "./ReviewRequest";
import { useUser } from "../../Context/UserContext";

const PracticeSequence = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
}: {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<React.SetStateAction<{ type: PracticeUpdateTypes }>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
}) => {
  const { openNotification } = React.useContext(adminContext);
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState<ISequenceReviewFormValues | null>(null);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();

  const saveSequenceAndReviewReq = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      let request: Promise<any> | null = null;

      if (isEdit.type === PracticeUpdateTypes.praticeSequences) {
        const params: ISequenceReviewInitialValues = {
          invoice: values.sequences.invoice,
          quote: values.sequences.quote,
          creditNote: values.sequences.creditNote,
          receipt: values.sequences.receipt,
          refund: values.sequences.refund,
        };
        request = PracticesService.updatePracticeSequence(practiceId, params);
      }

      if (isEdit.type === PracticeUpdateTypes.reviewRequest) {
        const params: {
          APCT: boolean;
          SA100: boolean;
          SA800: boolean;
          VAT: boolean;
        } = values.appSubFeature.reviewRequestConfig;
        request = PracticesService.updatePracticeReviewReq(practiceId, params);
      }
      if (!request) return;
      const res = await request;

      if (res?.result && res?.status) {
        openNotification("success", "updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        form.setFieldsValue(res.result);
        setInitialValues(res.result);
        getPracticeSequenceReviewDetails();
      } else {
        openNotification("error", res?.message || "Error updating practice details.");
      }
    } catch {
      openNotification("error", "Error updating practice details.");
    } finally {
      setLoading(false);
    }
  };

  const getPracticeSequenceReviewDetails = async () => {
    setLoading(true);
    try {
      const res = await PracticesService.getPracticeSequenceReviewDetails(practiceId);
      if (res?.result && res?.status) {
        const fieldValues = {
          sequences: {
            invoice: res.result.sequences.invoice,
            quote: res.result.sequences.quote,
            creditNote: res.result.sequences.creditNote,
            receipt: res.result.sequences.receipt,
            refund: res.result.sequences.refund,
          },
          appSubFeature: {
            reviewRequestConfig: {
              apct: res.result.reviewRequestConfig?.apct ?? false,
              sA100: res.result.reviewRequestConfig?.sA100 ?? false,
              sA800: res.result.reviewRequestConfig?.sA800 ?? false,
              vat: res.result.reviewRequestConfig?.vat ?? false,
            },
          },
        };
        form.setFieldsValue(fieldValues);
        setInitialValues(fieldValues);
      } else {
        openNotification("error", res?.message || "Error fetching practice sequence review details.");
      }
    } catch (error) {
      openNotification("error", "Something went wrong while fetching practice sequence review details.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getPracticeSequenceReviewDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);
  const handleCancel = () => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown })
  }
  return (
    <>
      <Form form={form} disabled={isViewOnly}>
        <Col className="card border pb-3 border-rounded-1 bg-secondary mb-4">
          <Row
            className="border-bottom  px-2 py-1 font-bold"
            justify="space-between"
          >
            Practice sequences
            {!isViewOnly &&
              <Button
                icon={<EditTwoTone />}
                type="text"
                // disabled={isInactive}
                style={{ border: "none" }}
                onClick={() =>
                  setIsEdit({ type: PracticeUpdateTypes.praticeSequences })
                }
              />
            }
          </Row>
          <Row className="px-3 pt-1" gutter={[10, 16]}>
            <Col xs={24} sm={12} md={8} xl={8} xxl={8}>
              <SequenceSection
                form={form}
                isEdit={isEdit}
                PracticeUpdateTypes={PracticeUpdateTypes.praticeSequences}
                title="Invoice"
                nameKey="invoice"
              />
            </Col>
            <Col md={8} xl={8} xxl={8}>
              <SequenceSection
                form={form}
                isEdit={isEdit}
                PracticeUpdateTypes={PracticeUpdateTypes.praticeSequences}
                title="Quotes"
                nameKey="quote"
              />
            </Col>
            <Col md={8} xl={8} xxl={8}>
              <SequenceSection
                form={form}
                isEdit={isEdit}
                PracticeUpdateTypes={PracticeUpdateTypes.praticeSequences}
                title="Credit note"
                nameKey="creditNote"
              />
            </Col>
          </Row>
          <Row className="px-3 pt-4" gutter={[10, 16]}>
            <Col md={8} xl={8} xxl={8}>
              <SequenceSection
                form={form}
                isEdit={isEdit}
                PracticeUpdateTypes={PracticeUpdateTypes.praticeSequences}
                title="Receipt"
                nameKey="receipt"
              />
            </Col>
            <Col md={8} xl={8} xxl={8}>
              <SequenceSection
                form={form}
                isEdit={isEdit}
                PracticeUpdateTypes={PracticeUpdateTypes.praticeSequences}
                title="Refund"
                nameKey="refund"
              />
            </Col>
          </Row>
          {!isViewOnly && isEdit.type === PracticeUpdateTypes.praticeSequences && (
            <Row className="px-3 pt-3">
              <Button type="primary" onClick={saveSequenceAndReviewReq}>
                <SaveOutlined /> Save
              </Button>
              <Button className="ms-2" onClick={handleCancel}>
                <CloseOutlined /> Cancel
              </Button>
            </Row>
          )}
        </Col>
      </Form>
      <ReviewRequest
        form={form}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        OnSaveClick={saveSequenceAndReviewReq}
        OnCancelClick={handleCancel}
      // handleValuesChange={handleValuesChange}
      // isInactive={inactive}
      />
    </>
  );
};

export default PracticeSequence;
